import type {
  AdvancedSearchMetric,
  AdvancedSearchProvider,
} from 'src/api/redux/advancedSearches';

const PAYER_MIX_METRIC_TYPE = 'payer_mix';
const REFERRAL_METRIC_TYPE = 'referral_volume';

const hasClaimsOrPatients = (metric: AdvancedSearchMetric): boolean =>
  (metric.display_options ?? []).some(
    (option) =>
      option.code.includes('claims') || option.code.includes('patients')
  );

const sumClaimsPatients = (
  providerMetrics: AdvancedSearchProvider['metrics'],
  metrics: AdvancedSearchMetric[]
): { claims: number; patients: number } => {
  let claims = 0;
  let patients = 0;

  for (const metric of metrics) {
    const metricValues = providerMetrics?.[String(metric.id)];
    if (!metricValues) continue;

    for (const option of metric.display_options ?? []) {
      const value = metricValues[option.code] || 0;
      if (option.code.includes('claims')) {
        claims += value;
      } else if (option.code.includes('patients')) {
        patients += value;
      }
    }
  }

  return { claims, patients };
};

// The list's claims and patients are the provider's own volume. Payer-mix claims are the
// claims matching the payer filter (they drive the percentage), not additional treating
// claims, and referral claims are a different measure, so neither is added in. A
// referral-only plan has no own volume, so it falls back to the referral numbers rather
// than showing zero.
/**
 * The Claims and Patients the Discovery list shows for an AI-search provider, summed
 * from its per-metric values. Shared with the map popups so both show the same numbers.
 */
const extractClaimsPatients = (
  providerMetrics: AdvancedSearchProvider['metrics'],
  metricHeaders: AdvancedSearchMetric[]
): { claims: number; patients: number } => {
  const ownVolumeMetrics = metricHeaders.filter(
    (metric) =>
      metric.execution_params?.type !== PAYER_MIX_METRIC_TYPE &&
      metric.execution_params?.type !== REFERRAL_METRIC_TYPE
  );

  if (ownVolumeMetrics.some(hasClaimsOrPatients)) {
    return sumClaimsPatients(providerMetrics, ownVolumeMetrics);
  }

  return sumClaimsPatients(
    providerMetrics,
    metricHeaders.filter(
      (metric) => metric.execution_params?.type === REFERRAL_METRIC_TYPE
    )
  );
};

export default extractClaimsPatients;
